/**
 * Warehouse location display helper: show the bin code (last meaningful segment).
 * Display-only — full paths are preserved for sorting, lookups, and stored values.
 *
 * Bins are stored two ways in the data:
 *  - as one dash-joined code:      'ZONE/5/F/AG02-1-001' -> 'AG02-1-001'
 *  - as a split slash hierarchy:   'ZONE/5/F/AD07/2/001' -> 'AD07-2-001'
 * For the split case we rejoin the trailing numeric segments (bin, shelf) with
 * their aisle code, stopping at the first non-numeric segment, so a bare-number
 * leaf like '001' shows as its real bin code 'AD07-2-001'.
 *
 * 'KC/ZONE/5/F/AD10-3-001' -> 'AD10-3-001', 'ZONE/5/F/BP/278' -> 'BP-278',
 * 'KC/KC' -> 'KC', '[出庫中]' -> '[出庫中]', null/'' -> ''.
 */
export function shortLocation(value: string | null | undefined): string {
  if (value == null) return '';
  const s = String(value);
  const segments = s.split('/').filter((seg) => seg.trim().length > 0);
  if (segments.length === 0) return s;
  const leaf = segments[segments.length - 1];
  // A "bare" bin leaf from a split hierarchy is digit-first with no dash
  // (e.g. '001', '002A'). Complete codes — letter-first ('AG02-1-001') or
  // already dash-joined ('3-FR01-01-01') — are shown as-is.
  if (!/^\d/.test(leaf) || leaf.includes('-')) return leaf;
  // Walk back up the split hierarchy, joining the numeric shelf segments and
  // the aisle code with dashes: ZONE/5/F/AS10/2/002A -> AS10-2-002A.
  const collected: string[] = [leaf];
  for (let i = segments.length - 2; i >= 0; i--) {
    const prev = segments[i];
    if (/^[A-Za-z]/.test(prev)) {
      // The aisle code (2+ chars) is the top of the bin code; a lone floor
      // marker like 'F' is not part of it, so stop without including it.
      if (prev.length >= 2) collected.unshift(prev);
      break;
    }
    collected.unshift(prev);
  }
  return collected.join('-');
}
